import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, getToken, setToken } from './api';
import { entrarSemConexao, guardarUsuario, lerUsuarioGuardado, useEmergencia, useSemConexao } from './emergencia';

const TEMPO_LIMITE_ME_MS = 6000;

export interface UsuarioLogado {
  id: number;
  nome: string;
  login: string;
  perfil: string;
  email: string | null;
  whatsapp_contact_id: number | null;
  departamentos: string[];
  opera_caixa?: boolean;
  vende_sem_turno?: boolean;
  /** Assinatura automatica nas mensagens de WhatsApp (gear do rodape do Atendimento). */
  assinatura_ativa?: boolean;
  /** Participa do rodizio de chamados agora (gear do rodape do Atendimento). Nao confundir com
   *  ausente_temporario, que e do admin e significa que a pessoa nao esta na loja. */
  recebe_chamados?: boolean;
  foto_url?: string | null;
  /** Conta de APARELHO compartilhado (o celular do balcao), nao de pessoa. Confinada no
   *  servidor a tela do Pix + AppCel, e la dentro responde qualquer conversa do app. */
  quiosque?: boolean;
  /** Tem jornada e ainda nao bateu a entrada (ou ja bateu a saida). Enquanto for `true` o
   *  servidor recusa `/api/*` inteiro — este campo so serve pra tela levar a pessoa pro
   *  registro do ponto em vez de um Dashboard em destrocos. Ver `ExigirPontoDoDia`. */
  ponto_pendente?: boolean;
}

interface AuthState {
  usuario: UsuarioLogado | null;
  loading: boolean;
  login: (login: string, senha: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioLogado | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    // Tempo-limite: o site nao passa pela Cloudflare, e com a VPS fora a conexao fica pendurada em
    // vez de falhar — sem isto a abertura esperaria o navegador desistir.
    const ctrl = new AbortController();
    const limite = setTimeout(() => ctrl.abort(), TEMPO_LIMITE_ME_MS);
    api.get<{ usuario: UsuarioLogado; token?: string }>('/api/auth/me', { signal: ctrl.signal })
      .then((r) => {
        // Token RENOVADO a cada abertura ([DA-563]): sem guardar o que volta aqui, a
        // renovacao do servidor nao serve pra nada e a sessao ainda venceria aos 30 dias.
        if (r.token) setToken(r.token);
        setUsuario(r.usuario);
        guardarUsuario(r.usuario);
      })
      .catch((e) => {
        // MODO EMERGENCIA: servidor que NAO RESPONDEU (rede, tempo-limite, 5xx) nao invalida a
        // sessao — ate 24/10/2026 qualquer falha aqui apagava o token, e com a VPS fora toda
        // tela acabava no login. Quem ja estava logado neste aparelho segue logado (pedido do
        // Ruan: aparelho sem ninguem logado NAO entra). Ponto: sem servidor nao ha como conferir,
        // entao a trava de ponto fica fora durante a queda. O modo em si so liga no botao da
        // tarja "Sem conexao" ([DA-849]).
        const semResposta = !(e instanceof ApiError) || e.status >= 500;
        const guardado = lerUsuarioGuardado<UsuarioLogado>();
        if (semResposta && guardado) {
          entrarSemConexao();
          setUsuario({ ...guardado, ponto_pendente: false });
          return;
        }
        setToken(null);
        guardarUsuario(null);
      })
      .finally(() => { clearTimeout(limite); setLoading(false); });
  }, []);

  // O servidor voltou com a sessao montada da copia: confere o token de verdade. Se ele morreu
  // durante a queda (corte das 05h, [DA-414]), o 401 leva pro login como sempre.
  // "Fora" = sem conexao OU com o modo emergencia ligado (nele as chamadas nem saem, e ele so
  // sai no botao "Voltar ao normal").
  const semConexao = useSemConexao();
  const emergencia = useEmergencia();
  const fora = semConexao || emergencia;
  const estavaSemConexao = useRef(fora);
  useEffect(() => {
    if (estavaSemConexao.current && !fora && getToken()) {
      api.get<{ usuario: UsuarioLogado; token?: string }>('/api/auth/me')
        .then((r) => {
          if (r.token) setToken(r.token);
          setUsuario(r.usuario);
          guardarUsuario(r.usuario);
        })
        .catch(() => { /* 401 ja redireciona no api.ts; outra falha: tenta na proxima abertura */ });
    }
    estavaSemConexao.current = fora;
  }, [fora]);

  // Sinaliza perfil do usuario logado no <html> pra CSS condicional eventual.
  useEffect(() => {
    const root = document.documentElement;
    if (usuario?.perfil) root.setAttribute('data-perfil', usuario.perfil);
    else root.removeAttribute('data-perfil');
  }, [usuario]);

  async function login(login: string, senha: string) {
    const r = await api.post<{ token: string; usuario: UsuarioLogado }>('/api/auth/login', { login, senha });
    setToken(r.token);
    setUsuario(r.usuario);
    guardarUsuario(r.usuario);
  }

  async function logout() {
    try { await api.post('/api/auth/logout'); } catch { /* nao bloquear */ }
    setToken(null);
    setUsuario(null);
    guardarUsuario(null);
  }

  return <AuthCtx.Provider value={{ usuario, loading, login, logout }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth fora de AuthProvider');
  return ctx;
}
